import { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { streamChat } from '../lib/api';

interface Props {
  onBack: () => void;
  onLogout: () => void;
  onOpenCookies?: () => void;
}

type ImgBlock = { type: 'image'; source: { type: 'base64'; media_type: string; data: string } };
type TxtBlock = { type: 'text'; text: string };
type Block = TxtBlock | ImgBlock;
type HMsg = { role: 'user' | 'assistant'; content: string | Block[] };

// Сжать фото до maxDim (jpeg) → base64 без префикса + data-url превью. Держит payload маленьким.
async function fileToImage(file: File, maxDim = 1024, quality = 0.82): Promise<{ data: string; media_type: string; preview: string }> {
  const dataUrl: string = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as string);
    fr.onerror = () => rej(new Error('read'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => rej(new Error('img'));
    im.src = dataUrl;
  });
  let w = img.width, h = img.height;
  if (Math.max(w, h) > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  const out = canvas.toDataURL('image/jpeg', quality);
  return { data: out.split(',')[1], media_type: 'image/jpeg', preview: out };
}

function tierColor(tier: string): string {
  if (tier === 'S') return 'text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]';
  if (tier === 'A') return 'text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]';
  if (tier === 'B') return 'text-sky-400';
  return 'text-gray-400';
}

function logBadge(type: string): string {
  if (type === 'food') return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
  if (type === 'workout') return 'bg-violet-500/20 text-violet-300 border border-violet-500/30';
  return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
}

function parseScore(raw: string) {
  try { return JSON.parse(raw); }
  catch { return { tier: '?', final: 0, verdict: '' }; }
}

const TIER_ORDER = ['S', 'A', 'B', 'C', 'F', '?'];

export function Health({ onBack, onLogout, onOpenCookies }: Props) {
  const [data, setData] = useState<{ logs: any[]; topFoods: any[]; favorites: string[] } | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  
  const [activeTab, setActiveTab] = useState<string>('Избранное');

  // Chat state (как в основном чате)
  const [messages, setMessages] = useState<HMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [model, setModel] = useState('opus');
  const [effort, setEffort] = useState('high');
  const [usePreset, setUsePreset] = useState(true);
  const [useTools, setUseTools] = useState(true);
  const [pendingImg, setPendingImg] = useState<{ data: string; media_type: string; preview: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  async function handleFile(file?: File | null) {
    if (!file || !file.type.startsWith('image/')) return;
    try { setPendingImg(await fileToImage(file)); setChatError(null); }
    catch { setChatError('не удалось загрузить фото'); }
  }

  // Дневник по датам
  const todayStr = new Date().toLocaleDateString('sv-SE');
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [dayData, setDayData] = useState<{ date: string; logs: any[]; totals: any; targets: any } | null>(null);
  const [logDates, setLogDates] = useState<string[]>([]);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const loadDay = (date: string) => {
    fetch('/api/health-data/day?date=' + date, { credentials: 'include' })
      .then((r) => r.json()).then(setDayData).catch((e) => console.error(e));
  };
  const loadDates = () => {
    fetch('/api/health-data/dates', { credentials: 'include' })
      .then((r) => r.json()).then((d) => setLogDates(d.dates || [])).catch((e) => console.error(e));
  };
  const deleteLog = (id: string) => {
    fetch('/api/health-data/log/' + id, { method: 'DELETE', credentials: 'include' })
      .then(() => { loadDay(selectedDate); loadDates(); loadData(); })
      .catch((e) => console.error(e));
  };
  const refreshDiary = () => { loadDay(selectedDate); loadDates(); };

  // Вкладки
  const [view, setView] = useState<'assistant' | 'food'>('assistant');

  // Память клона
  const [memories, setMemories] = useState<Array<{ id: string; kind: string; text: string }>>([]);
  const loadMemory = () => {
    fetch('/api/health-data/memory', { credentials: 'include' })
      .then((r) => r.json()).then((d) => setMemories(d.memories || [])).catch((e) => console.error(e));
  };
  const deleteMemory = (id: string) => {
    fetch('/api/health-data/memory/' + id, { method: 'DELETE', credentials: 'include' })
      .then(() => loadMemory()).catch((e) => console.error(e));
  };

  // Беседы (несколько чатов; память/логи/еда — общие)
  const [conversations, setConversations] = useState<Array<{ id: string; title: string | null; msg_count: number; updated_at: string }>>([]);
  const [currentConvId, setCurrentConvId] = useState<string | null>(null);
  const loadConversations = () => {
    fetch('/api/health-data/conversations', { credentials: 'include' })
      .then((r) => r.json()).then((d) => setConversations(d.conversations || [])).catch((e) => console.error(e));
  };
  const loadChatFor = (convId: string) => {
    fetch('/api/health-data/chat?conversation=' + convId, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setMessages((d.messages || []).map((m: any) => ({ role: m.role, content: m.content }))))
      .catch((e) => console.error(e));
  };
  const ensureConv = async (): Promise<string> => {
    if (currentConvId) return currentConvId;
    const r = await fetch('/api/health-data/conversations', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const d = await r.json();
    setCurrentConvId(d.id); loadConversations();
    return d.id;
  };
  const newChat = async () => {
    const r = await fetch('/api/health-data/conversations', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const d = await r.json();
    setCurrentConvId(d.id); setMessages([]); setChatError(null); loadConversations();
  };
  const switchChat = (id: string) => { setCurrentConvId(id); setChatError(null); loadChatFor(id); };
  const deleteChat = (id: string) => {
    fetch('/api/health-data/conversations/' + id, { method: 'DELETE', credentials: 'include' })
      .then(() => { if (id === currentConvId) { setCurrentConvId(null); setMessages([]); } loadConversations(); })
      .catch((e) => console.error(e));
  };
  const saveMsg = (convId: string, role: string, content: string) => {
    fetch('/api/health-data/chat', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ conversation: convId, role, content }) })
      .then(() => loadConversations()).catch(() => {});
  };

  useEffect(() => { loadDates(); loadMemory(); loadConversations(); }, []);
  useEffect(() => { if (!currentConvId && conversations.length) switchChat(conversations[0].id); }, [conversations]);
  useEffect(() => { loadDay(selectedDate); }, [selectedDate]);

  const loadData = () => {
    fetch('/api/health-data/today', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        if (activeTab === 'Избранное' && (!d.favorites || d.favorites.length === 0)) {
           // If no favorites, set to first available store
           const stores = Array.from(new Set(d.topFoods.map((f: any) => f.store || 'Другие')));
           if (stores.length > 0) setActiveTab(stores[0] as string);
        }
      })
      .catch((e) => console.error(e));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function handleSearch() {
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    fetch('/api/health-data/foods?q=' + encodeURIComponent(search.trim()) + '&limit=50', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setSearchResults(d.foods || []))
      .catch((e) => console.error(e));
  }

  async function handleSendChat() {
    if ((!chatInput.trim() && !pendingImg) || chatLoading) return;
    const userText = chatInput.trim();
    let content: string | Block[];
    if (pendingImg) {
      content = [
        { type: 'text', text: userText || 'Определи еду на фото и залогируй.' },
        { type: 'image', source: { type: 'base64', media_type: pendingImg.media_type, data: pendingImg.data } },
      ];
    } else {
      content = userText;
    }
    setChatInput('');
    setPendingImg(null);
    setChatError(null);
    const userStore = typeof content === 'string' ? content : (userText || 'Определи еду на фото и залогируй.') + ' [фото]';
    const convId = await ensureConv();
    saveMsg(convId, 'user', userStore);
    const newMsgs: HMsg[] = [...messages, { role: 'user', content }];
    setMessages(newMsgs);
    setChatLoading(true);

    let asstContent = '';
    setMessages([...newMsgs, { role: 'assistant', content: '' }]);

    try {
      const stream = streamChat({
        messages: newMsgs as any,
        model,
        effort: effort as any,
        preset_ids: usePreset ? ['health'] : [],
        health_context: usePreset,
        tools: useTools ? ['health_log_food', 'health_ingest', 'health_search_food'] : undefined,
      });

      for await (const ev of stream) {
        if (ev.event === 'text') {
          const d = JSON.parse(ev.data) as { delta?: string };
          if (d.delta) {
            asstContent += d.delta;
            setMessages([...newMsgs, { role: 'assistant', content: asstContent }]);
          }
        } else if (ev.event === 'error') {
          const d = JSON.parse(ev.data) as { message?: string; body?: string; status?: number };
          setChatError(d.message || d.body || (d.status ? `upstream ${d.status}` : 'ошибка сервера'));
          break;
        } else if (ev.event === 'done') {
          break;
        }
      }
      if (asstContent) saveMsg(convId, 'assistant', asstContent);
      loadData(); refreshDiary(); loadMemory(); // мог добавиться лог/факт памяти
    } catch (e) {
      setChatError((e as Error).message);
    } finally {
      setChatLoading(false);
      // убрать пустой пузырь ассистента, если ничего не пришло (была ошибка)
      setMessages((cur) => {
        const last = cur[cur.length - 1];
        if (last && last.role === 'assistant' && !last.content) return cur.slice(0, -1);
        return cur;
      });
    }
  }

  const toggleFavorite = async (id: string, currentlyFav: boolean) => {
    if (!data) return;
    // Optimistic UI
    const newFavs = currentlyFav ? data.favorites.filter(x => x !== id) : [...data.favorites, id];
    setData({ ...data, favorites: newFavs });
    
    try {
      await fetch('/api/health-data/favorites/' + id, {
        method: currentlyFav ? 'DELETE' : 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Failed to toggle favorite', e);
      loadData(); // revert
    }
  };

  // Grouping logic for Tabs
  const stores = data ? Array.from(new Set(data.topFoods.map(f => f.store || 'Другие'))) : [];
  const allTabs = ['Избранное', ...stores];

  // Get active foods
  let activeFoods: any[] = [];
  if (data) {
    if (activeTab === 'Избранное') {
      activeFoods = data.topFoods.filter(f => data.favorites?.includes(f.id));
    } else {
      activeFoods = data.topFoods.filter(f => (f.store || 'Другие') === activeTab);
    }
  }

  // Group active foods by Tier
  const foodsByTier: Record<string, any[]> = {};
  for (const f of activeFoods) {
    const score = parseScore(f.score_json);
    const t = score.tier || '?';
    if (!foodsByTier[t]) foodsByTier[t] = [];
    foodsByTier[t].push(f);
  }

  return (
    <div className="min-h-full flex flex-col bg-gray-950 text-gray-100 font-sans">
      <Header
        account={null}
        model="opus"
        onClear={() => {}}
        onLogout={onLogout}
        onOpenCookies={onOpenCookies}
      />

      <div className="flex-1 p-4 sm:p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-[1400px] mx-auto space-y-8 pb-12">
          
          {/* Title bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-gradient-to-br from-emerald-400 to-cyan-500 bg-clip-text text-transparent drop-shadow-sm">
              Health Ecosystem
            </h1>
            <button
              onClick={onBack}
              className="text-sm font-medium text-gray-300 bg-gray-800/80 hover:bg-gray-700 hover:text-white px-5 py-2.5 rounded-full transition-all shadow-md flex items-center gap-2 border border-gray-700/50"
            >
              ← Chat View
            </button>
          </div>

          {/* Вкладки */}
          <div className="flex gap-2">
            {(['assistant', 'food'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-5 py-2.5 rounded-2xl text-sm font-semibold transition ${view === v ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/60 border border-gray-700/50'}`}>
                {v === 'assistant' ? '💬 Ассистент' : '🍱 Еда'}
              </button>
            ))}
          </div>

          {view === 'assistant' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            
            {/* ЛЕВО: беседы + чат */}
            <div className="xl:col-span-2 flex flex-col gap-6">

              {/* Беседы */}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => void newChat()} className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold shadow shrink-0">+ Новый чат</button>
                {conversations.map((c) => (
                  <button key={c.id} onClick={() => switchChat(c.id)}
                    className={`group flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm border transition ${c.id === currentConvId ? 'bg-gray-700 border-emerald-500/50 text-white' : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:bg-gray-700/50'}`}>
                    <span className="max-w-[140px] truncate">{c.title || 'Без названия'}</span>
                    <span onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 cursor-pointer">✕</span>
                  </button>
                ))}
              </div>

              {/* Quick Chat */}
              <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/60 p-5 rounded-[2rem] shadow-2xl flex flex-col h-[500px]">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-emerald-400 flex items-center gap-2">💬 Health Assistant</h2>
                  <span className="text-xs text-gray-500 truncate max-w-[180px]">{conversations.find((c) => c.id === currentConvId)?.title || 'новый чат'}</span>
                </div>
                <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 custom-scrollbar">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 text-sm mt-10">
                      Напишите "съел плов 200г", "тренировка час" или спросите про питание.
                    </div>
                  ) : (
                    messages.map((m, i) => (
                      <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                        <div className={`p-3 rounded-2xl max-w-[90%] text-sm whitespace-pre-wrap ${
                          m.role === 'user' ? 'bg-emerald-600/80 text-white rounded-br-none' : 'bg-gray-800/80 text-gray-200 border border-gray-700/50 rounded-bl-none'
                        }`}>
                          {typeof m.content === 'string'
                            ? m.content
                            : m.content.map((b, j) => b.type === 'text'
                                ? <span key={j}>{b.text}</span>
                                : <img key={j} src={`data:${b.source.media_type};base64,${b.source.data}`} className="mt-1 rounded-lg max-h-48 border border-gray-700/50" />)}
                        </div>
                      </div>
                    ))
                  )}
                  {chatError && (
                    <div className="p-3 rounded-2xl bg-red-950/50 border border-red-900/60 text-red-300 text-sm">
                      ⚠️ {chatError}
                    </div>
                  )}
                  {chatLoading && <div className="text-gray-500 text-xs animate-pulse">Thinking...</div>}
                  <div ref={messagesEndRef} />
                </div>
                {/* Параметры (как в основном чате) */}
                <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                  <select value={model} onChange={(e) => setModel(e.target.value)}
                    className="bg-gray-800/60 border border-gray-700/50 rounded-lg px-2 py-1 text-gray-200 focus:outline-none focus:border-emerald-500/50">
                    <option value="opus">opus</option>
                    <option value="sonnet">sonnet</option>
                    <option value="haiku">haiku</option>
                  </select>
                  <select value={effort} onChange={(e) => setEffort(e.target.value)}
                    className="bg-gray-800/60 border border-gray-700/50 rounded-lg px-2 py-1 text-gray-200 focus:outline-none focus:border-emerald-500/50">
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                    <option value="max">max</option>
                  </select>
                  <button onClick={() => setUsePreset((v) => !v)} title="системный промпт Health"
                    className={`px-2 py-1 rounded-lg border transition ${usePreset ? 'bg-emerald-600/30 border-emerald-500/50 text-emerald-200' : 'bg-gray-800/50 border-gray-700/50 text-gray-400'}`}>
                    🩺 health
                  </button>
                  <button onClick={() => setUseTools((v) => !v)} title="логировать еду/трени в дневник"
                    className={`px-2 py-1 rounded-lg border transition ${useTools ? 'bg-emerald-600/30 border-emerald-500/50 text-emerald-200' : 'bg-gray-800/50 border-gray-700/50 text-gray-400'}`}>
                    📝 лог
                  </button>
                </div>
                {pendingImg && (
                  <div className="mb-2 relative inline-block">
                    <img src={pendingImg.preview} alt="preview" className="h-16 rounded-lg border border-gray-700/50" />
                    <button onClick={() => setPendingImg(null)}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gray-900 border border-gray-600 text-gray-300 text-[10px] flex items-center justify-center hover:bg-red-600 hover:text-white">✕</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { void handleFile(e.target.files?.[0]); e.target.value = ''; }} />
                  <button onClick={() => fileInputRef.current?.click()} title="Прикрепить фото еды"
                    className="px-3 py-2.5 bg-gray-800/60 border border-gray-700/50 rounded-xl hover:bg-gray-700/60 transition text-lg leading-none">📎</button>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                    onPaste={(e) => { const f = Array.from(e.clipboardData.files).find((x) => x.type.startsWith('image/')); if (f) { void handleFile(f); e.preventDefault(); } }}
                    placeholder='Съел плов 200г  ·  или фото 📎'
                    className="flex-1 bg-gray-800/60 text-sm border border-gray-700/50 rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-500/50"
                  />
                  <button
                    onClick={handleSendChat}
                    disabled={chatLoading}
                    className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold rounded-xl transition shadow-lg shadow-emerald-500/20"
                  >
                    ➤
                  </button>
                </div>
              </div>
            </div>

            {/* ПРАВО: память клона + дневник */}
            <div className="xl:col-span-1 flex flex-col gap-6">

              {/* Память клона */}
              {memories.length > 0 && (
                <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/60 p-5 rounded-[2rem] shadow-2xl">
                  <h2 className="text-lg font-bold mb-3 text-emerald-400 flex items-center gap-2">🧠 Обо мне <span className="text-xs font-normal text-gray-500">память клона</span></h2>
                  <ul className="space-y-1.5 max-h-[220px] overflow-y-auto custom-scrollbar pr-1">
                    {memories.map((m) => (
                      <li key={m.id} className="group flex items-start justify-between gap-2 text-sm text-gray-300">
                        <span className="min-w-0"><span className="text-emerald-500/70 mr-1">•</span>{m.text}</span>
                        <button onClick={() => deleteMemory(m.id)} title="Забыть" className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 shrink-0 text-xs">✕</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Дневник: календарь + шкала макросов + логи дня */}
              <DiaryPanel
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                dayData={dayData}
                logDates={logDates}
                calMonth={calMonth}
                setCalMonth={setCalMonth}
                onDeleteLog={deleteLog}
                todayStr={todayStr}
              />
            </div>
          </div>
          )}

          {view === 'food' && (
          <div className="space-y-6">
              
              {/* Search */}
              <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/60 p-5 rounded-[2rem] shadow-2xl flex gap-3">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Искать еду (например, плов, гречка)..."
                  className="flex-1 bg-gray-800/60 text-gray-100 border border-gray-700/50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50"
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition shadow-lg shadow-cyan-500/20"
                >
                  Search
                </button>
              </div>

              {/* Foods Display */}
              <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/60 p-6 sm:p-8 rounded-[2rem] shadow-2xl min-h-[500px]">
                {searchResults !== null ? (
                  <>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-cyan-400">Search Results</h2>
                      <button onClick={() => setSearchResults(null)} className="text-gray-500 hover:text-gray-300 transition text-sm">Clear</button>
                    </div>
                    {searchResults.length === 0 ? <p className="text-gray-500 text-center py-10">Ничего не найдено.</p> : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {searchResults.map(f => (
                          <FoodCard 
                            key={f.id} 
                            f={f} 
                            isFav={data?.favorites?.includes(f.id) || false}
                            onToggleFav={(id, b) => toggleFavorite(id, b)} 
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!data ? (
                      <div className="animate-pulse space-y-3"><div className="h-20 bg-gray-800/50 rounded-xl"></div></div>
                    ) : (
                      <div className="flex flex-col h-full">
                        {/* Tabs */}
                        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-800/60 pb-4">
                          {allTabs.map(tab => (
                            <button 
                              key={tab} 
                              onClick={() => setActiveTab(tab)}
                              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                activeTab === tab 
                                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20' 
                                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-gray-200 border border-gray-700/30'
                              }`}
                            >
                              {tab === 'Избранное' ? '⭐ ' : '🛒 '}{tab}
                            </button>
                          ))}
                        </div>

                        {/* Tier List for active tab */}
                        {activeFoods.length === 0 ? (
                          <div className="text-center text-gray-500 py-12 bg-gray-800/20 rounded-2xl border border-gray-700/30 border-dashed">
                            {activeTab === 'Избранное' ? 'Вы еще ничего не добавили в избранное.' : 'В этом магазине пока пусто.'}
                          </div>
                        ) : (
                          <div className="space-y-8 pb-4">
                            {TIER_ORDER.map(tier => {
                              const foods = foodsByTier[tier];
                              if (!foods || foods.length === 0) return null;
                              return (
                                <div key={tier} className="bg-gray-900/20 rounded-2xl p-4 sm:p-6 border border-gray-800/60">
                                  <div className="flex items-center gap-3 mb-4">
                                    <span className={`text-2xl font-black ${tierColor(tier)}`}>{tier} Tier</span>
                                    <div className="h-px bg-gray-800 flex-1"></div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {foods.map(f => (
                                      <FoodCard 
                                        key={f.id} 
                                        f={f} 
                                        isFav={data.favorites?.includes(f.id)}
                                        onToggleFav={(id, b) => toggleFavorite(id, b)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

          </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MacroBar({ label, cur, target, color }: { label: string; cur: number; target?: number; color: string }) {
  const c = cur || 0;
  const pct = target ? (c / target) * 100 : 0;
  const over = !!target && c > target;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-300 font-medium">{label}</span>
        <span className="text-gray-400 font-mono">{Math.round(c)}{target ? ` / ${target} · ${Math.round(pct)}%` : ''}</span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-800/80 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${over ? 'bg-amber-400' : color}`} style={{ width: Math.min(100, pct) + '%' }} />
      </div>
    </div>
  );
}

function MacroBars({ totals, targets }: { totals: any; targets: any }) {
  const t = totals || { kkal: 0, b: 0, z: 0, u: 0 };
  const g = targets || {};
  return (
    <div className="space-y-3">
      <MacroBar label="Калории" cur={t.kkal} target={g.kkal} color="bg-emerald-500" />
      <MacroBar label="Белок, г" cur={t.b} target={g.protein_g} color="bg-sky-500" />
      <MacroBar label="Жиры, г" cur={t.z} target={g.fat_g} color="bg-violet-500" />
      <MacroBar label="Углеводы, г" cur={t.u} target={g.carb_g} color="bg-orange-500" />
    </div>
  );
}

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function Calendar({ selected, onSelect, marked, month, setMonth, today }: {
  selected: string; onSelect: (d: string) => void; marked: Set<string>;
  month: { y: number; m: number }; setMonth: (m: { y: number; m: number }) => void; today: string;
}) {
  const first = new Date(month.y, month.m, 1);
  const startWd = (first.getDay() + 6) % 7;
  const days = new Date(month.y, month.m + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(`${month.y}-${String(month.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  const prev = () => setMonth(month.m === 0 ? { y: month.y - 1, m: 11 } : { y: month.y, m: month.m - 1 });
  const next = () => setMonth(month.m === 11 ? { y: month.y + 1, m: 0 } : { y: month.y, m: month.m + 1 });
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button onClick={prev} className="px-2 py-1 rounded-lg hover:bg-gray-700/50 text-gray-400">‹</button>
        <span className="text-sm font-semibold text-gray-200">{MONTHS[month.m]} {month.y}</span>
        <button onClick={next} className="px-2 py-1 rounded-lg hover:bg-gray-700/50 text-gray-400">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WD.map((w) => <div key={w} className="text-[10px] text-gray-500 font-medium">{w}</div>)}
        {cells.map((ds, i) => ds === null ? <div key={'e' + i} /> : (
          <button key={ds} onClick={() => onSelect(ds)}
            className={`relative aspect-square rounded-lg text-xs flex items-center justify-center transition
              ${ds === selected ? 'bg-emerald-600 text-white font-bold' : 'hover:bg-gray-700/50 text-gray-300'}
              ${ds === today && ds !== selected ? 'ring-1 ring-emerald-500/50' : ''}`}>
            {parseInt(ds.slice(8), 10)}
            {marked.has(ds) && ds !== selected && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-emerald-400" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function DiaryPanel({ selectedDate, onSelectDate, dayData, logDates, calMonth, setCalMonth, onDeleteLog, todayStr }: {
  selectedDate: string; onSelectDate: (d: string) => void; dayData: any; logDates: string[];
  calMonth: { y: number; m: number }; setCalMonth: (m: { y: number; m: number }) => void;
  onDeleteLog: (id: string) => void; todayStr: string;
}) {
  const marked = new Set(logDates);
  const logs: any[] = dayData?.logs || [];
  const foodLogs = logs.filter((l) => l.type === 'food');
  const otherLogs = logs.filter((l) => l.type !== 'food');
  const parse = (s: string) => { try { return JSON.parse(s); } catch { return {}; } };
  const dateLabel = selectedDate === todayStr ? 'Сегодня'
    : new Date(selectedDate + 'T00:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' });
  return (
    <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/60 p-5 rounded-[2rem] shadow-2xl flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-emerald-400">📖 Дневник</h2>
        {selectedDate !== todayStr && (
          <button onClick={() => onSelectDate(todayStr)} className="text-xs text-emerald-400 hover:text-emerald-300">→ сегодня</button>
        )}
      </div>

      <Calendar selected={selectedDate} onSelect={onSelectDate} marked={marked} month={calMonth} setMonth={setCalMonth} today={todayStr} />

      <div className="border-t border-gray-800/60 pt-4">
        <div className="text-sm font-semibold text-gray-200 mb-3 capitalize">{dateLabel}</div>
        <MacroBars totals={dayData?.totals} targets={dayData?.targets} />
      </div>

      <div className="max-h-[280px] overflow-y-auto custom-scrollbar pr-1">
        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm italic">Нет записей за этот день.</p>
        ) : (
          <ul className="space-y-2">
            {foodLogs.map((log) => {
              const d = parse(log.data);
              return (
                <li key={log.id} className="group p-2.5 bg-gray-800/40 rounded-xl border border-gray-700/50 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-gray-200 truncate">{d.name || 'еда'} {d.grams ? <span className="text-gray-500">{d.grams}г</span> : null} {d.tier && <span className="text-[10px] text-emerald-400">[{d.tier}]</span>}</div>
                    <div className="text-[11px] font-mono text-gray-500">Б{Math.round(d.b || 0)} Ж{Math.round(d.z || 0)} У{Math.round(d.u || 0)} · {Math.round(d.kkal || 0)} ккал{d.meal ? ' · ' + d.meal : ''}</div>
                  </div>
                  <button onClick={() => onDeleteLog(log.id)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition shrink-0">✕</button>
                </li>
              );
            })}
            {otherLogs.map((log) => (
              <li key={log.id} className="group p-2.5 bg-gray-800/40 rounded-xl border border-gray-700/50 text-sm flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className={'px-2 py-0.5 text-[10px] font-bold uppercase rounded ' + logBadge(log.type)}>{log.type}</span>
                  <span className="text-gray-300 ml-2 break-words">{typeof log.data === 'string' ? log.data : JSON.stringify(log.data)}</span>
                </div>
                <button onClick={() => onDeleteLog(log.id)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition shrink-0">✕</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FoodCard({ f, isFav, onToggleFav }: { f: any, isFav: boolean, onToggleFav: (id: string, current: boolean) => void }) {
  const score = parseScore(f.score_json);
  return (
    <div className="flex flex-col bg-gray-800/40 border border-gray-700/50 rounded-xl overflow-hidden hover:bg-gray-800/70 hover:border-gray-600/50 transition-all group relative">
      
      {/* Fav button */}
      <button 
        onClick={(e) => { e.stopPropagation(); onToggleFav(f.id, isFav); }}
        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-gray-900/60 backdrop-blur flex items-center justify-center border border-gray-700/50 hover:bg-gray-700 transition"
      >
        {isFav ? '⭐' : <span className="opacity-50 hover:opacity-100">🤍</span>}
      </button>

      <div className="flex p-3 gap-3 pr-10">
        {f.image ? (
          <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-white border border-gray-700/50 relative">
            <img src={f.image} alt={f.name} className="w-full h-full object-contain absolute inset-0 m-auto mix-blend-multiply" />
          </div>
        ) : (
          <div className="w-16 h-16 shrink-0 rounded-lg bg-gray-800 flex items-center justify-center text-xl border border-gray-700/50">🍱</div>
        )}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div className="flex justify-between items-start gap-1">
            <span className="text-[13px] font-medium text-gray-200 leading-snug line-clamp-2" title={f.name}>{f.name}</span>
          </div>
          <div className="flex justify-between items-end text-[11px] font-mono text-gray-500 mt-1">
            <span>Б:{f.belki} Ж:{f.zhiry} У:{f.uglevody}</span>
            <span className="text-emerald-400/80 font-sans font-semibold shrink-0">{f.price ? f.price + '₽' : ''}</span>
          </div>
        </div>
      </div>
      {score.verdict && (
        <div className="px-3 pb-2 text-[10px] text-gray-400 line-clamp-2 border-t border-gray-800/50 pt-1.5 bg-gray-900/40 group-hover:bg-gray-900/60 transition-colors" title={score.verdict}>
          {score.verdict}
        </div>
      )}
    </div>
  );
}
